import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { CustomerQrActions, CustomerQrState } from "./customer-qr-controller";

/** Big enough to scan off a screen or a printed card at arm's length. */
const QR_PIXELS = 320;

/**
 * The shop's customer entry point, as a QR code and as a link.
 *
 * This is the door customers come through: they scan it on the counter and land
 * in the customer PWA already inside this shop. The same URL is offered as text
 * so it can go in a message, an email or a printed sign. Renders nothing while
 * the section is closed, so a page mounts it unconditionally.
 */
export function CustomerQrModal() {
  const state = useSubtree(CustomerQrState);
  const observer = useObserver();
  const storeId = observer.watch(state.storeId);
  if (!storeId) return null;
  return <Dialog />;
}

function Dialog() {
  const state = useSubtree(CustomerQrState);
  const actions = useSubtree(CustomerQrActions);
  const observer = useObserver();
  const url = observer.watch(state.url);
  const status = observer.watch(state.status);

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    // Rendered in the browser: the URL is not a secret, and this avoids both a
    // round trip and an image-generating dependency on the server.
    QRCode.toDataURL(url, { width: QR_PIXELS, margin: 2, errorCorrectionLevel: "M" })
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && actions.close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actions]);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // The OS share sheet, where there is one. Absent on most desktop browsers, so Copy is always offered.
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const share = async () => {
    if (!url) return;
    try {
      await navigator.share({ title: "Scan cards in our shop", url });
    } catch {
      // A dismissed share sheet rejects; nothing to report.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* The backdrop is a real button: dismissing by clicking away has to be reachable by keyboard too. */}
      <button type="button" aria-label="Close" onClick={actions.close} className="absolute inset-0 bg-black/70" />
      <div className="modal relative w-full max-w-sm p-6" role="dialog" aria-modal="true" aria-label="Customer QR code">
        <h2 className="text-base font-semibold text-white">Your customer QR code</h2>
        <p className="mt-1 text-sm text-muted">
          Customers scan this to open your shop and start scanning cards. No app install, no account.
        </p>

        <div className="mt-5 flex justify-center">
          {/* Stays white while the rest of the app is near-black: a scanner needs that contrast. */}
          <div className="flex h-[320px] w-[320px] items-center justify-center rounded-row border border-line bg-white">
            {status.kind === "error" ? (
              <span className="px-6 text-center text-sm text-danger">Couldn't load your shop link.</span>
            ) : qrDataUrl ? (
              <img src={qrDataUrl} alt="QR code for this shop" width={QR_PIXELS} height={QR_PIXELS} />
            ) : (
              <span className="text-sm text-black/40">Generating…</span>
            )}
          </div>
        </div>

        {url && <p className="mt-4 break-all rounded-btn bg-surface px-3 py-2 text-center text-xs text-muted">{url}</p>}

        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" onClick={copy} disabled={!url} className="btn-primary btn-sm flex-1">
            {copied ? "Copied" : "Copy link"}
          </button>
          {canShare && (
            <button type="button" onClick={share} disabled={!url} className="btn-secondary btn-sm flex-1">
              Share
            </button>
          )}
          {qrDataUrl && (
            <a href={qrDataUrl} download="shop-qr-code.png" className="btn-secondary btn-sm flex-1">
              Download
            </a>
          )}
        </div>

        <button type="button" onClick={actions.close} className="btn-ghost mt-3 w-full justify-center">
          Close
        </button>
      </div>
    </div>
  );
}
